import { z } from 'zod';
import type { DocumentDto } from './documents.js';
import { INVOICE_EMAIL_BODY_MAX_LENGTH } from './invoice-email.js';

/**
 * Contrat réseau du module d'impression.
 *
 * Une route d'impression ne renvoie **jamais les octets** : elle renvoie les métadonnées du
 * document rangé au dossier du patient, et le client va chercher le contenu par
 * `GET /api/documents/:id/content` ou par une URL signée. C'est la même règle que pour un fichier
 * téléversé — aucune route ne sert un chemin, tout passe par une ligne `documents` visible sous RLS.
 * La V1 imprimait à la volée dans la réponse HTTP et ne rangeait rien : une facture réimprimée
 * deux ans plus tard sortait avec les identifiants fiscaux du jour.
 */

export const pdfIdParamSchema = z.object({ id: z.uuid() }).strict();

/**
 * `?force=1` réimprime une pièce déjà imprimée. Sans ce drapeau, une facture dont le document
 * existe déjà est renvoyée telle quelle : réimprimer sans raison créerait un doublon au dossier.
 */
export const pdfGenerateQuerySchema = z
  .object({
    force: z
      .enum(['0', '1'])
      .default('0')
      .transform((value) => value === '1'),
  })
  .strict();
export type PdfGenerateQuery = z.infer<typeof pdfGenerateQuerySchema>;

/** Métadonnées de la pièce produite. Le contenu se récupère par le module documents. */
export interface PdfDocumentBody {
  document: DocumentDto;
}

/**
 * Envoi d'une facture par email. Sans `to`, l'adresse du patient est utilisée ; si le dossier n'en
 * porte aucune, la route répond 422 `patient_has_no_email` plutôt que d'inventer un destinataire.
 */
export const invoiceSendSchema = z
  .object({
    to: z.email().max(320).optional(),
    /**
     * Corps tel que le dialogue l'a laissé, y compris vide. Absent : le modèle du cabinet, ou le
     * texte du produit. La chaîne vide n'est pas effacée : l'expéditeur a retiré le texte.
     */
    message: z.string().trim().max(INVOICE_EMAIL_BODY_MAX_LENGTH).optional(),
  })
  .strict();
export type InvoiceSendInput = z.infer<typeof invoiceSendSchema>;

/**
 * Corps déjà rempli pour le dialogue d'envoi. Les variables sont celles de cette facture, dans la
 * langue du patient. Ce n'est pas l'aperçu des paramètres, qui reste une facture d'exemple.
 */
export interface InvoiceSendDraftDto {
  message: string;
}

/** Une ligne du journal des envois. L'adresse n'apparaît que là, jamais dans l'audit ni les logs. */
export interface InvoiceSendingDto {
  id: string;
  invoiceId: string;
  documentId: string | null;
  sentTo: string;
  sentAt: string;
  sentBy: string | null;
}

export interface InvoiceSendBody {
  sending: InvoiceSendingDto;
  document: DocumentDto;
}
